import React from 'react';
import { Card, Spin, InputNumber, Space, Alert, Drawer, Typography, Tag, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { PmsAPI } from '../../../controller/PmsAPI';
import { buildLists, RAISE_CEILING, HORIZON_DAYS } from './rules';
import { targetSummary } from './target';
import { summaryRows, DRAWER_META, RAISE_CEILING_REASON } from './summary';
import { fetchSpecialDates } from '../../../controller/holidayCalendar';
import PricingActionsTable, { drawerWidthFor } from './PricingActionsTables';
import { FONT } from "../../styles/typography";
import { FAINT_COLOR, MUTED_COLOR } from '../../components/metricColors';

const TARGET_KEY = 'ops.targetOcc';

/**
 * 未來定價動作。
 *
 * 自行載入資料而非由 OpsDashboard 傳入：這張卡的範圍固定為未來 90 天，
 * 與儀錶板的檢視粒度 (月/季/年) 無關，混在同一個 fetch 只會讓兩邊的生命週期互相牽制。
 */
export default class PricingActionsCard extends React.Component {
  /** 三列摘要的標籤顏色。灰色代表「這項今天沒事要做」，一眼掃得出哪幾項要處理。 */
  static TONE_COLOR = { pos: 'green', warn: 'orange', none: 'default' };


  state = { items: [], loading: true, error: null, target: 70, specialDates: new Set(), drawerKind: null };

  componentDidMount() {
    try {
      const v = Number(window.localStorage.getItem(TARGET_KEY));
      if (v >= 10 && v <= 100) this.setState({ target: v });
    } catch (e) { /* localStorage 不可用時用預設 */ }
    this.load();
  }

  componentWillUnmount() {
    // 目前只有 componentDidMount 呼叫一次 load()，卡片內沒有會重複觸發它的互動，
    // 現在不會出事。這裡是防未來被包進條件渲染或重掛載的容器時，
    // 卸載後才回來的非同步回應仍寫入 state，不是修現有的 bug。
    this._unmounted = true;
  }

  setTarget = (v) => {
    const target = Math.max(10, Math.min(100, Math.round(Number(v) || 70)));
    this.setState({ target });
    try { window.localStorage.setItem(TARGET_KEY, String(target)); } catch (e) { /* 忽略 */ }
  };

  load = () => {
    const f = (m) => m.format('YYYY/MM/DD');
    const from = moment().add(1, 'day');
    const to = moment().add(HORIZON_DAYS, 'days');
    this.setState({ loading: true, error: null });
    PmsAPI.ops.pricingActions(f(from), f(to), 5)
      .then((r) => { if (!this._unmounted) this.setState({ items: r.data.items || [] }); })
      .catch((e) => { if (!this._unmounted) this.setState({ error: (e && e.message) || '載入失敗' }); })
      .finally(() => { if (!this._unmounted) this.setState({ loading: false }); });
    // 假日只影響調漲列的建議晚數 (特殊假日 3 晚、其餘 2 晚)，獨立載入、失敗不影響清單；
    // fetchSpecialDates 目前永遠 resolve (holidayCalendar.js 內部吞掉錯誤保證不 reject)，
    // 但那是跨模組的隱性假設，這裡顯式接住失敗：拿不到假日就退回一律 2 晚，
    // 不值得讓這個非關鍵資訊的失敗變成 unhandled rejection、更不該讓整張清單消失。
    fetchSpecialDates(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
      .then((specialDates) => { if (!this._unmounted) this.setState({ specialDates }); })
      .catch(() => {});
  };

  openDrawer = (kind) => this.setState({ drawerKind: kind });
  closeDrawer = () => this.setState({ drawerKind: null });

  /** 整列可點就必須鍵盤也能觸發 —— 明細只剩這一個入口，只綁滑鼠等於對鍵盤使用者關門。 */
  onRowKeyDown = (kind) => (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    this.openDrawer(kind);
  };

  render() {
    const { items, loading, error, target } = this.state;
    const { raise, lower } = buildLists(items, {
      target, todayIso: moment().format('YYYY-MM-DD'), specialDates: this.state.specialDates,
    });
    const sum = targetSummary(items, { target });
    const rows = summaryRows({ sum, raise, lower, target, horizonDays: HORIZON_DAYS });
    // 點哪一列就開哪一類的 Drawer；drawerKind 為 null 時整個 Drawer 收起來
    const kind = this.state.drawerKind;
    const drawer = kind ? { kind, ...DRAWER_META[kind], rows: { raise, lower }[kind] } : null;

    return (
      <Card size="small"
        title={<span>未來定價動作</span>}
        extra={
          <Space size={4}>
            <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>目標住房率</span>
            <InputNumber size="small" aria-label="目標住房率 (%)" min={10} max={100} value={target} onChange={this.setTarget}
              formatter={(v) => `${v}%`} parser={(v) => String(v).replace('%', '')} style={{ width: 80 }} />
            {/* 調漲上界不開放調整，但要讓人看得到：目標旋鈕就在旁邊，
                不寫出來會以為調漲也跟著目標走。理由放 Tooltip，Drawer 說明有同一段全文 */}
            <span style={{ fontSize: FONT.caption, color: MUTED_COLOR, marginLeft: 8, whiteSpace: 'nowrap' }}>
              調漲上界 {RAISE_CEILING}%
              <Tooltip title={RAISE_CEILING_REASON} trigger={['hover', 'focus']}>
                <QuestionCircleOutlined aria-label="調漲上界的說明" tabIndex={0}
                  style={{ marginLeft: 4, color: FAINT_COLOR, cursor: 'help' }} />
              </Tooltip>
            </span>
          </Space>
        }>
        <Spin spinning={loading}>
          {error ? <Alert type="error" showIcon message={`定價建議載入失敗：${error}`} /> : null}
          {/* 載入中或載入失敗時不渲染三列 —— 那些句子是肯定語氣的結論 ("沒有該調漲的日子")，
              在還沒拿到資料時說出來就是錯的，而且改版後它們是整張卡的全部內容，誤導性更高 */}
          {error || (loading && !items.length) ? null : rows.map((r) => (
            <div key={r.key}
              role={r.clickable ? 'button' : undefined}
              tabIndex={r.clickable ? 0 : undefined}
              onClick={r.clickable ? () => this.openDrawer(r.key) : undefined}
              onKeyDown={r.clickable ? this.onRowKeyDown(r.key) : undefined}
              style={{
                display: 'flex', gap: 8, alignItems: 'baseline',
                padding: '6px 0', borderTop: r.key === 'target' ? undefined : '1px solid #f5f5f5',
                cursor: r.clickable ? 'pointer' : undefined,
                opacity: r.tone === 'none' ? 0.55 : 1,
              }}>
              <Tag color={PricingActionsCard.TONE_COLOR[r.tone]} style={{ marginInlineEnd: 0 }}>{r.tag}</Tag>
              <Typography.Text style={{ flex: 1, minWidth: 0 }}>{r.text}</Typography.Text>
              {r.clickable ? <span style={{ color: FAINT_COLOR }}>›</span> : null}
            </div>
          ))}
        </Spin>
        {/* 寬度依 kind 而非寫死：調漲、調降各 14 欄，同一個寬度不可能都合身。
            見 drawerWidthFor —— 寫死 1000px 時調降表有五欄落在畫面外。 */}
        <Drawer title={drawer ? `${drawer.title} · ${drawer.rows.length} 天` : null}
          placement="right" width={drawer ? drawerWidthFor(drawer.kind) : undefined}
          visible={drawer != null} onClose={this.closeDrawer}>
          {drawer ? (
            <>
              <PricingActionsTable kind={drawer.kind} rows={drawer.rows} target={target}
                onPickDate={this.props.onPickDate} />
              <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 8 }}>{drawer.note}</div>
            </>
          ) : null}
        </Drawer>
      </Card>
    );
  }
}
